import React from "react";

import {FormEntry, IconButton} from "controls/react/form";
import {AntInput} from "controls/react/ant/antInput";
import {DataRegistryWindow} from "areas/management/data-registry/dataRegistryWindow";
import {inputWithAction} from "controls/react/form/common";
import {ApplicationState} from "framework/applicationState";
import {Api} from "tools/api";
import {UnorderedListOutlined, DeleteOutlined} from "@ant-design/icons";
import {AntButton} from "controls/react/ant/antButton";
import {DesignerDataRegistryWindowUserSettingsKey} from "controls/designer/constants";

const i = require('core/localization/localization').translator({
	'Select metric': {},
	'{0} and {1} more': {},
	'Clear selection': {}
});

const b = inputWithAction

export type SingleMetricSelectorProps = {
	accountId?: string;
	value?: string;
	onChange?: (value: string) => void;
	multiple?: boolean
}

export function useMetricName(metricId: string|string[]) {
	const [metricName, setMetricName] = React.useState('');

	React.useEffect(() => {
		if(!metricId || metricId.length == 0 ){
			setMetricName('')
			return;
		}

		Api.fetchPost(Api.accountRoot() + "metrics/registeredMetrics", Array.isArray(metricId) ? metricId: [metricId])
			.then(response => {
				if (response.length == 0 || response.success === false)
					return;

				let item = response[0];
				let name = '';
				if (item.assetName) {
					name += item.assetName + '/';
				}
				name += item.categoryNode + '/' + item.instanceName;

				if(Array.isArray(metricId) && metricId.length > 1){
					name = i('{0} and {1} more', name, metricId.length - 1)
				}
				setMetricName(name);
			})
	}, [metricId])

	return metricName;
}

export const SingleMetricSelector = (props: SingleMetricSelectorProps) => {
	const [showWindow, setShowWindow] = React.useState(false);

	const onAdd = React.useCallback((ids: string[]) => {
		props.onChange?.(ids.length ? ids[0] : null);
		setShowWindow(false);
	}, [props.onChange]);

	const metricName = useMetricName(props.value);

	return <div className={b()}>
		<AntInput value={metricName} readOnly={true} className={b('input')}/>
		<AntButton icon={<UnorderedListOutlined/>}
		           className={b('action')}
		           size={"small"}
		           onClick={() => setShowWindow(true)}
		/>

		<AntButton icon={<DeleteOutlined/>}
		           className={b('action')}
		           size={"small"}
		           title={i('Clear selection')}
		           onClick={() => props.onChange?.(null)}
		/>
		{ showWindow && <DataRegistryWindow
			onClose={() => setShowWindow(false)}
			onAdd={onAdd}
			userSettingsKey={DesignerDataRegistryWindowUserSettingsKey}
			accountId={props.accountId ?? ApplicationState.accountId}
			singleMetricSelection={true}
			defaultSelectedMetricIds={props.value ? [props.value] : []}
		/>
		}
	</div>
}

export type MultipleMetricsSelectorProps = {
	accountId?: string;
	value?: string[];
	onChange?: (value: string[]) => void;
	limit?: number
}

export const MultipleMetricsSelector = (props: MultipleMetricsSelectorProps) => {
	const [showWindow, setShowWindow] = React.useState(false);

	const metricName = useMetricName(props.value);
	const onAdd = React.useCallback((ids: string[]) => {
		props.onChange(ids);
		setShowWindow(false);
	}, [])

	return <div className={b()}>
		<AntInput value={metricName} readOnly={true} className={b('input')}/>
		<IconButton iconName={"show-thumbnails"} embedded={true} containerClass={b('action')}
		            onClick={() => setShowWindow(true)}
		            title={i('Select metric')}/>
		<IconButton iconName={"eraser"} embedded={true} containerClass={b('action')}
		            onClick={() => props.onChange([])}
		            title={i('Clear selection')}/>
		{ showWindow &&
			<DataRegistryWindow
				limit={9999}
				onClose={() => setShowWindow(false)}
				onAdd={onAdd}
				accountId={props.accountId ?? ApplicationState.accountId}
				defaultSelectedMetricIds={props.value}
				userSettingsKey={DesignerDataRegistryWindowUserSettingsKey}
		/>
		}
	</div>
}
